import {ViewController} from "data/types/structure";
import {action, makeAutoObservable, observable} from "mobx";
import {inject, injectable} from "inversify";
import {IRankingsTableObject} from "data/types/entities";
import {Bindings} from "data/constants/bindings";
import type {ILeaguesStore} from "data/stores/leagues/leagues.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IRound} from "data/providers/json/json.provider";
import type {Empty} from "data/types/generics";
import {DirectionFilter, RankingsFiltersType} from "data/enums";
import type {ILeaderboardFilters} from "views/pages/leaderboard/leaderboard.controller";

interface IParams {
	leagueId: number;
}

export interface ILeagueLadderController extends ViewController<IParams> {
	get isLoading(): boolean;
	get rankings(): IRankingsTableObject;
	get nextPage(): boolean;
	get rounds(): IRound[];
	get selectRound(): number | null;
	get filters(): ILeaderboardFilters;

	roundChange: (roundId: IRound | undefined) => void;
	loadMoreHandler: () => void;
	changeFilters: (type: RankingsFiltersType) => void;
}

@injectable()
export class LeagueLadderController implements ILeagueLadderController {
	@observable private _leagueId: Empty<number>;
	@observable private _selectedRound: number | null = null;
	@observable private _isLoading: boolean = false;
	@observable private _filters: ILeaderboardFilters = {
		direction: DirectionFilter.Low,
		type: RankingsFiltersType.Rank,
	};
	private columnsDefaultSort = {
		[RankingsFiltersType.Rank]: DirectionFilter.Low,
		[RankingsFiltersType.Points]: DirectionFilter.High,
		[RankingsFiltersType.Margin]: DirectionFilter.High,
	};

	constructor(
		@inject(Bindings.LeaguesStore) private _leaguesStore: ILeaguesStore,
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore
	) {
		makeAutoObservable(this);
	}

	get isLoading(): boolean {
		return this._isLoading;
	}

	get selectedRound(): number {
		return this._roundsStore.currentRound?.id || 0;
	}

	get rounds(): IRound[] {
		return this._roundsStore.completedAndActiveRounds;
	}

	get nextPage(): boolean {
		return this._leaguesStore.standingsNextPage;
	}

	get rankings(): IRankingsTableObject {
		return this._leaguesStore.standings;
	}

	get selectRound(): number | null {
		return this._selectedRound;
	}

	get filters() {
		return this._filters;
	}

	@action
	public loadMoreHandler = (): void => {
		void this._leaguesStore.loadMoreLeagueStandings(
			this._selectedRound,
			this._leagueId,
			this._filters.direction,
			this._filters.type
		);
	};

	@action
	public roundChange = (round: IRound | undefined): void => {
		this._roundsStore.setSelectedRound(round);
		this._selectedRound = round?.id || null;
		this.fetchStandings();
	};

	dispose(): void {
		return;
	}

	init(param: IParams): void {
		this._leagueId = param.leagueId;
		void this._roundsStore.fetchRounds();
		this.fetchStandings();
	}

	@action
	protected fetchStandings() {
		this._isLoading = true;
		this._leaguesStore
			.fetchLeagueStandings(
				this._selectedRound,
				this._leagueId,
				this._filters.direction,
				this._filters.type
			)
			.finally(() => {
				this._isLoading = false;
			});
	}

	changeFilters = (type: RankingsFiltersType) => {
		if (type === this._filters.type) {
			this._filters = {
				...this._filters,
				direction:
					this._filters.direction === DirectionFilter.Low
						? DirectionFilter.High
						: DirectionFilter.Low,
			};
		} else {
			this._filters = {
				type,
				direction: this.columnsDefaultSort[type],
			};
		}

		this.fetchStandings();
	};
}
